import { Box } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { INTEGRATION_SUMMARY } from "../../../../../theme/tokens";
import { formatTimeRange } from "../../../../../utils/dateUtils";
import type { IntegrationShiftsSummary, ShiftMismatchRow, ShiftMismatchType } from "../../../../../utils/models";
import { displayNullable, formatHours, gridSx } from "./consts";
import { EmptySummaryState } from "./EmptySummaryState";
import { SectionHeading, sectionHeadingCountProps } from "./SectionHeading";

const MISMATCH_TYPE_KEYS: Record<ShiftMismatchType, string> = {
	no_schedule: "integration.summary.shifts.mismatch.noSchedule",
	no_actual: "integration.summary.shifts.mismatch.noPunches",
};

const TEST_ID = "hris-shifts-summary-mismatches";

export const ShiftMismatchesGrid: FC<{ section: IntegrationShiftsSummary["mismatches"] }> = ({ section }) => {
	const { t, i18n } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = chosenDepartment;
	const columns = useMemo<GridColDef<ShiftMismatchRow>[]>(
		() => [
			{ field: "fullName", headerName: t("integration.summary.column.employee"), flex: 1, minWidth: 150 },
			{
				field: "jobName",
				headerName: t("integration.summary.column.job"),
				flex: 0.8,
				minWidth: 110,
				valueGetter: (_value, row) => displayNullable(row.jobName),
			},
			{
				field: "mismatchType",
				headerName: t("integration.summary.shifts.column.mismatchType"),
				flex: 0.9,
				minWidth: 160,
				valueGetter: (_value, row) => t(MISMATCH_TYPE_KEYS[row.mismatchType]),
			},
			{
				field: "day",
				headerName: t("integration.summary.shifts.column.day"),
				width: 130,
				// Sort on the instant, display the localized date.
				valueGetter: (_value, row) => row.day.toMillis(),
				valueFormatter: (_value, row) => row.day.toLocaleString(DateTime.DATE_MED),
			},
			{
				field: "startTime",
				headerName: t("integration.summary.shifts.column.shift"),
				flex: 0.9,
				minWidth: 160,
				valueGetter: (_value, row) => row.startTime.toMillis(),
				valueFormatter: (_value, row) => formatTimeRange(row.startTime.setZone(timezone), row.endTime.setZone(timezone)),
			},
			{
				field: "hours",
				headerName: t("integration.summary.shifts.column.hours"),
				width: 90,
				valueFormatter: (_value, row) => formatHours(row.hours, i18n.language),
			},
		],
		[i18n.language, t, timezone],
	);
	return (
		<>
			<SectionHeading
				title={t("integration.summary.shifts.mismatches.title")}
				description={t("integration.summary.shifts.mismatches.description")}
				{...sectionHeadingCountProps(section)}
			/>
			<Box sx={gridSx} data-testid={`${TEST_ID}-container`}>
				<SystemDataGrid<ShiftMismatchRow>
					rows={section.rows}
					columns={columns}
					getRowId={(row) => `${row.mismatchType}-${row.shiftId}`}
					searchableColumns={["fullName"]}
					dataTestId={TEST_ID}
					containedHeight={{ maxHeight: INTEGRATION_SUMMARY.GRID_HEIGHT }}
					emptyState={
						<EmptySummaryState
							title={t("integration.summary.shifts.noMismatches.title")}
							hint={t("integration.summary.shifts.noMismatches.hint")}
							testId={`${TEST_ID}-empty`}
						/>
					}
				/>
			</Box>
		</>
	);
};
